;(() => {
  const defaultSettings = {
    currency: {
      symbol: "$",
      format: "%s%v",
      decimal: ".",
      thousand: ",",
      precision: 2,
      grouping: 3,
    },
    number: {
      precision: 0,
      grouping: 3,
      thousand: ",",
      decimal: ".",
    },
  }

  const isArray = Array.isArray
  const isObject = (value) =>
    value && Object.prototype.toString.call(value) === "[object Object]"

  const defaults = (object, defs) => {
    const result = { ...(object || {}) }
    Object.keys(defs || {}).forEach((key) => {
      if (result[key] == null) {
        result[key] = defs[key]
      }
    })
    return result
  }

  const checkPrecision = (value, fallback) => {
    const normalized = Math.round(Math.abs(value))
    return Number.isNaN(normalized) ? fallback : normalized
  }

  const unformat = (value, decimal) => {
    if (isArray(value)) {
      return value.map((entry) => unformat(entry, decimal))
    }

    if (typeof value === "number") {
      return Number.isNaN(value) ? 0 : value
    }

    const normalizedDecimal = decimal || settings.number.decimal
    const regex = new RegExp(`[^0-9-${normalizedDecimal}]`, "g")
    const normalized = String(value || 0).replace(
      /\(([^)]*)\)/g,
      (match, inner) => (/\d/.test(inner) ? `-${inner}` : inner),
    )
    const unformatted = parseFloat(
      normalized.replace(regex, "").replace(normalizedDecimal, "."),
    )

    return Number.isNaN(unformatted) ? 0 : unformatted
  }

  const toFixed = (value, precision) => {
    const usePrecision = checkPrecision(precision, settings.number.precision)
    const exponentialForm = Number(`${unformat(value)}e${usePrecision}`)
    const rounded = Math.round(exponentialForm)
    return Number(`${rounded}e-${usePrecision}`).toFixed(usePrecision)
  }

  const checkCurrencyFormat = (format) => {
    const defaultFormat = settings.currency.format
    if (typeof format === "function") return checkCurrencyFormat(format())

    if (typeof format === "string" && format.includes("%v")) {
      return {
        pos: format,
        neg: format.replace("-", "").replace("%v", "-%v"),
        zero: format,
      }
    }

    if (!format || !format.pos || !format.pos.includes("%v")) {
      if (typeof defaultFormat !== "string") return defaultFormat

      const formatted = {
        pos: defaultFormat,
        neg: defaultFormat.replace("%v", "-%v"),
        zero: defaultFormat,
      }
      settings.currency.format = formatted
      return formatted
    }

    return format
  }

  const formatNumber = (value, precision, thousand, decimal) => {
    if (isArray(value)) {
      return value.map((entry) =>
        formatNumber(entry, precision, thousand, decimal),
      )
    }

    const number = unformat(value)
    const opts = defaults(
      isObject(precision)
        ? precision
        : {
            precision,
            thousand,
            decimal,
          },
      settings.number,
    )

    const usePrecision = checkPrecision(
      opts.precision,
      settings.number.precision,
    )
    const negative = number < 0 ? "-" : ""
    const base = parseInt(toFixed(Math.abs(number || 0), usePrecision), 10) + ""
    const mod = base.length > 3 ? base.length % 3 : 0
    const thousands = opts.thousand
    const decimals = opts.decimal

    const formatted =
      negative +
      (mod ? base.substr(0, mod) + thousands : "") +
      base.substr(mod).replace(/(\d{3})(?=\d)/g, `$1${thousands}`) +
      (usePrecision
        ? decimals + toFixed(Math.abs(number), usePrecision).split(".")[1]
        : "")

    return formatted
  }

  const formatMoney = (
    number,
    symbol,
    precision,
    thousand,
    decimal,
    format,
  ) => {
    if (isArray(number)) {
      return number.map((entry) =>
        formatMoney(entry, symbol, precision, thousand, decimal, format),
      )
    }

    const value = unformat(number)
    const opts = defaults(
      isObject(symbol)
        ? symbol
        : {
            symbol,
            precision,
            thousand,
            decimal,
            format,
          },
      settings.currency,
    )
    const formats = checkCurrencyFormat(opts.format)
    const useFormat =
      value > 0 ? formats.pos : value < 0 ? formats.neg : formats.zero

    return useFormat
      .replace("%s", opts.symbol)
      .replace(
        "%v",
        formatNumber(
          Math.abs(value),
          checkPrecision(opts.precision, settings.currency.precision),
          opts.thousand,
          opts.decimal,
        ),
      )
  }

  const settings = JSON.parse(JSON.stringify(defaultSettings))

  window.accounting = {
    version: "0.4.1",
    settings,
    formatMoney,
    formatNumber,
    unformat,
    toFixed,
  }
})()
